/**
 * Session Management Store for Enhanced Segmentation Agent v2.0
 * 
 * This store manages session save/load functionality, session persistence,
 * import/export capabilities, and session management utilities.
 * 
 * Extracted from segmentationStore.ts to provide focused session management
 * capabilities with better organization and testability.
 * 
 * Author: Enhanced Segmentation Agent v2.0
 * Date: June 26, 2025
 */

import { create } from 'zustand'
import type { 
  SavedSession,
  TextFile,
  TriggerConfig,
  PreprocessingResult,
  LLMConfig,
  ProcessingSession,
  ChunkProcessingResult
} from '../types/segmentationTypes'

// ============================================================================
// Session Store State Interface
// ============================================================================

interface SessionStoreState {
  // Session state
  savedSessions: SavedSession[]
  sessionLoading: boolean
  sessionError: string | null
  
  // Actions
  saveSession: (name: string, sessionData: {
    uploadedFile: TextFile | null,
    triggerConfig: TriggerConfig,
    preprocessingResult: PreprocessingResult | null,
    selectedChunks: string[],
    llmConfig: LLMConfig,
    chunkResults: Map<string, ChunkProcessingResult>,
    processingSession: ProcessingSession | null
  }) => void
  loadSession: (sessionId: string) => SavedSession | null
  deleteSession: (sessionId: string) => void
  exportSession: (sessionId: string) => void
  importSession: (data: any) => void
  clearSessionError: () => void
  refreshSessions: () => void
  
  // Session utilities
  getSessionById: (sessionId: string) => SavedSession | null
  getSessionsByName: (name: string) => SavedSession[]
  getRecentSessions: (limit?: number) => SavedSession[]
  getSessionCount: () => number
  validateSessionData: (data: any) => boolean
}

// ============================================================================
// Session Store Implementation
// ============================================================================

export const useSessionStore = create<SessionStoreState>((set, get) => {
  // Load saved sessions from localStorage on initialization
  let initialSavedSessions: SavedSession[] = []
  try {
    const saved = localStorage.getItem('segmentation_saved_sessions')
    if (saved) {
      const parsed = JSON.parse(saved)
      initialSavedSessions = parsed.map((session: any) => ({
        ...session,
        chunkResults: new Map(session.chunkResults || [])
      }))
    }
  } catch (error) {
    console.error('Failed to load saved sessions from localStorage:', error)
  }

  return {
    // Initial state
    savedSessions: initialSavedSessions,
    sessionLoading: false,
    sessionError: null,

    // ============================================================================
    // Session Save/Load Operations
    // ============================================================================

    saveSession: (name: string, sessionData) => {
      set({ sessionLoading: true, sessionError: null })
      
      try {
        const sessionId = `session_${Date.now()}`
        const savedSession: SavedSession = {
          id: sessionId,
          name,
          timestamp: Date.now(),
          uploadedFile: sessionData.uploadedFile,
          triggerConfig: sessionData.triggerConfig,
          preprocessingResult: sessionData.preprocessingResult,
          selectedChunks: sessionData.selectedChunks,
          llmConfig: sessionData.llmConfig,
          chunkResults: sessionData.chunkResults,
          processingSession: sessionData.processingSession
        }

        set(state => {
          const newSessions = [...state.savedSessions, savedSession]
          
          // Persist to localStorage
          try {
            const sessionsToSave = newSessions.map(session => ({
              ...session,
              chunkResults: Array.from(session.chunkResults.entries())
            }))
            localStorage.setItem('segmentation_saved_sessions', JSON.stringify(sessionsToSave))
            console.log('Session saved successfully:', sessionId, name)
          } catch (error) {
            console.error('Failed to save session to localStorage:', error)
            throw error
          }

          return { 
            savedSessions: newSessions,
            sessionLoading: false
          }
        })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to save session'
        set({ 
          sessionError: errorMessage,
          sessionLoading: false 
        })
        console.error('Failed to save session:', error)
      }
    },

    loadSession: (sessionId: string): SavedSession | null => {
      try {
        const state = get()
        const session = state.savedSessions.find(s => s.id === sessionId)
        
        if (!session) {
          set({ sessionError: `Session not found: ${sessionId}` })
          return null
        }

        console.log('Session loaded successfully:', sessionId, session.name)
        return session
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load session'
        set({ sessionError: errorMessage })
        console.error('Failed to load session:', error)
        return null
      }
    },

    deleteSession: (sessionId: string) => {
      try {
        set(state => {
          const newSessions = state.savedSessions.filter(s => s.id !== sessionId)

          // Update localStorage
          try {
            const sessionsToSave = newSessions.map(session => ({
              ...session,
              chunkResults: Array.from(session.chunkResults.entries())
            }))
            localStorage.setItem('segmentation_saved_sessions', JSON.stringify(sessionsToSave))
            console.log('Session deleted successfully:', sessionId)
          } catch (error) {
            console.error('Failed to update localStorage after session deletion:', error)
            throw error
          }

          return { savedSessions: newSessions }
        })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete session'
        set({ sessionError: errorMessage })
        console.error('Failed to delete session:', error)
      }
    },

    // ============================================================================
    // Session Import/Export
    // ============================================================================

    exportSession: (sessionId: string) => {
      try {
        const state = get()
        const session = state.savedSessions.find(s => s.id === sessionId)
        
        if (!session) {
          set({ sessionError: `Session not found for export: ${sessionId}` })
          return
        }

        const exportData = {
          ...session,
          chunkResults: Array.from(session.chunkResults.entries())
        }

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `segmentation_session_${session.name}_${new Date(session.timestamp).toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
        
        console.log('Session exported successfully:', sessionId, session.name)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to export session'
        set({ sessionError: errorMessage })
        console.error('Failed to export session:', error)
      }
    },

    importSession: (data: any) => {
      set({ sessionLoading: true, sessionError: null })
      
      try {
        const { validateSessionData } = get()
        if (!validateSessionData(data)) {
          throw new Error('Invalid session data format')
        }

        const session: SavedSession = {
          ...data,
          id: `imported_${Date.now()}`,
          timestamp: Date.now(),
          chunkResults: new Map(data.chunkResults || [])
        }

        set(state => {
          const newSessions = [...state.savedSessions, session]
          
          // Update localStorage
          try {
            const sessionsToSave = newSessions.map(s => ({
              ...s,
              chunkResults: Array.from(s.chunkResults.entries())
            }))
            localStorage.setItem('segmentation_saved_sessions', JSON.stringify(sessionsToSave))
            console.log('Session imported successfully:', session.id, session.name)
          } catch (error) {
            console.error('Failed to persist imported session:', error)
            throw error
          }
          
          return { 
            savedSessions: newSessions,
            sessionLoading: false
          }
        })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to import session'
        set({ 
          sessionError: errorMessage,
          sessionLoading: false 
        })
        console.error('Failed to import session:', error)
      }
    },

    // ============================================================================
    // Session Utilities
    // ============================================================================

    getSessionById: (sessionId: string): SavedSession | null => {
      const state = get()
      return state.savedSessions.find(s => s.id === sessionId) || null
    },

    getSessionsByName: (name: string): SavedSession[] => {
      const state = get()
      return state.savedSessions.filter(s => 
        s.name.toLowerCase().includes(name.toLowerCase())
      )
    },

    getRecentSessions: (limit = 10): SavedSession[] => {
      const state = get()
      return state.savedSessions
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, limit)
    },

    getSessionCount: (): number => {
      const state = get()
      return state.savedSessions.length
    },

    validateSessionData: (data: any): boolean => {
      try {
        // Check required fields
        if (!data || typeof data !== 'object') return false
        if (!data.name || typeof data.name !== 'string') return false
        if (!data.triggerConfig || typeof data.triggerConfig !== 'object') return false
        if (!data.llmConfig || typeof data.llmConfig !== 'object') return false
        
        // Optional fields validation
        if (data.selectedChunks && !Array.isArray(data.selectedChunks)) return false
        if (data.chunkResults && !Array.isArray(data.chunkResults)) return false
        
        return true
      } catch (error) {
        console.error('Error validating session data:', error)
        return false
      }
    },

    // ============================================================================
    // Utility Actions
    // ============================================================================

    clearSessionError: () => {
      set({ sessionError: null })
    },

    refreshSessions: () => {
      try {
        const saved = localStorage.getItem('segmentation_saved_sessions')
        if (saved) {
          const parsed = JSON.parse(saved)
          const sessions = parsed.map((session: any) => ({
            ...session,
            chunkResults: new Map(session.chunkResults || [])
          }))
          set({ savedSessions: sessions })
          console.log('Sessions refreshed from localStorage:', sessions.length)
        }
      } catch (error) {
        console.error('Failed to refresh sessions from localStorage:', error)
        set({ sessionError: 'Failed to refresh sessions' })
      }
    }
  }
})

// ============================================================================
// Export for use in other components
// ============================================================================

export default useSessionStore